
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Define the Review type to be exported
export interface Review {
  id: number;
  rating: number;
  comment: string | null;
  created_at: string;
  customer_id: string | null;
  service_id: number | null;
  highlighted: boolean;
  customer_name?: string | null;
  customer_photo?: string | null;
}

export const useReviewsQuery = (serviceId: number) => {
  const { toast } = useToast();

  const { data: reviewsData, isLoading, error, refetch } = useQuery({
    queryKey: ['reviews', serviceId],
    queryFn: async () => {
      try {
        console.log('Fetching reviews for service:', serviceId);
        
        // Fetch reviews first
        const { data: reviews, error } = await supabase
          .from('reviews')
          .select(`
            id,
            rating,
            comment,
            created_at,
            customer_id,
            service_id,
            highlighted
          `)
          .eq('service_id', serviceId)
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching reviews:', error);
          throw error;
        }

        // If there are no reviews, return empty array immediately
        if (!reviews || reviews.length === 0) {
          console.log(`No reviews found for service ${serviceId}`);
          return [];
        }

        console.log('Raw reviews data:', reviews);

        // If we have reviews with customer_ids, fetch their profiles
        const processedReviews = await Promise.all((reviews || []).map(async (review) => {
          // Default values
          let customerName = "Anonymous";
          let customerPhoto = null;
          
          // Only try to get profile data if we have a customer_id
          if (review.customer_id) {
            try {
              const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('display_name, photo_url')
                .eq('id', review.customer_id)
                .maybeSingle();
              
              if (profileError) {
                console.error('Error fetching profile:', profileError);
              } else if (profileData) {
                customerName = profileData.display_name || "Anonymous";
                customerPhoto = profileData.photo_url;
              }
            } catch (err) {
              console.error('Error in profile fetch:', err);
            }
          }
          
          // Ensure highlighted is ALWAYS a boolean (true/false)
          const highlightedValue = review.highlighted === true;
          
          // Return the review with profile data added
          return {
            ...review,
            highlighted: highlightedValue,
            customer_name: customerName,
            customer_photo: customerPhoto
          };
        }));
        
        console.log(`Fetched ${processedReviews.length || 0} reviews for service ${serviceId}`, 
          processedReviews.map(r => ({ 
            id: r.id, 
            highlighted: r.highlighted, 
            type: typeof r.highlighted 
          }))
        );
        
        return processedReviews;
      } catch (error) {
        console.error('Error in useReviewsQuery:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load reviews"
        });
        // Return empty array instead of null to prevent filtering errors
        return [];
      }
    },
    staleTime: 30 * 1000, // Further reduce stale time to 30 seconds for this page
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window gets focus
    retry: 2, // Retry failed requests twice
    retryDelay: 1000, // Wait 1 second between retries
  });

  // Safely handle filtering by ensuring reviewsData is an array
  const reviews = reviewsData || [];
  
  // Use strict boolean comparison for highlighting
  const highlightedReviews = reviews.filter(review => review.highlighted === true);
  
  // Regular reviews are those that are not highlighted
  const regularReviews = reviews.filter(review => review.highlighted !== true);
  
  console.log('Reviews query result:', { 
    total: reviews.length, 
    highlighted: highlightedReviews.length, 
    regular: regularReviews.length,
    highlightedIds: highlightedReviews.map(r => r.id)
  });

  return {
    reviews,
    highlightedReviews,
    regularReviews,
    isLoading,
    error,
    refetch
  };
};
